'use client'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useRouter } from 'next/navigation'

import type {
  CartItemProps,
  GravityFormsAPIConditionalLogicProps,
  GravityFormsAPIFieldProps,
  GravityFormsApiProps,
} from '@typings'
import {
  BOOKING_FORM_TITLES,
  HIDDEN_FIELDS,
  REQUEST_FORM_TITLES,
  TRAVELPLAN_FIELD_ID,
} from '@constants/gravity-forms.constants'
import { useCart } from '@contexts/cart.context'
import { renderForm } from '@services/index.services'
import { travelPlan } from '@services/index.services'
import { GA4Events } from '@utilities/ga4-helpers/GA4Events.helpers'
import { gfCreateTransactionId } from '@utilities/gravity-forms-helpers/gf-create-transaction-id.helpers'
import { gfGetAccordionFields } from '@utilities/gravity-forms-helpers/gf-get-accordion-fields'
import { gfGetFieldCurrentVisibility } from '@utilities/gravity-forms-helpers/gf-get-field-current-visibility.helpers'
import { gfGetRequiredFieldMessage } from '@utilities/gravity-forms-helpers/gf-get-required-field-message.helpers'
import { gfLoopConditionalRules } from '@utilities/gravity-forms-helpers/gf-loop-conditional-rules.helpers'
import { gfParseFieldValuesType } from '@utilities/gravity-forms-helpers/gf-parse-fieldvalues-type.helpers'
import { sanitizeInput } from '@utilities/gravity-forms-helpers/gf-sanitize-input.helpers'
import {
  gfClearPreviouslySavedForm,
  gfMakeSaveFormId,
  gfRetrieveSavedForm,
  gfSaveForm,
} from '@utilities/gravity-forms-helpers/gf-save-form.helpers'
import { gfTransformFieldValuesToPayload } from '@utilities/gravity-forms-helpers/gf-transform-fieldvalues-to-payload.helpers'
import { gfTransformPages } from '@utilities/gravity-forms-helpers/gf-transform-pages.helpers'
import { gfValidateField } from '@utilities/gravity-forms-helpers/gf-validate-field.helpers'
import { getClientsideQueryString } from '@utilities/helpers/get-clientside-query-string.helpers'
import { getUserId } from '@utilities/helpers/get-user-id.helpers'
import { scrollToTop } from '@utilities/helpers/scroll-to-top.helpers'
import { stripWpUrl } from '@utilities/helpers/strip-wp-url.helpers'

import { useUi } from './ui.context'
import { useWishlist } from './wishlist.context'

import { API } from '@/api/index.api'
import { dispatchRollbarError } from '@/utilities/helpers/dispatchRollbarError.helpers'
import { translate } from '@/utilities/helpers/translate.helpers'

const _default = null

export const FormContext = createContext(_default)
FormContext.displayName = 'FormContext'

export const FormContextProvider = ({
  children,
  destination,
  GTM_ID,
  ROLLBAR_ENV,
  ROLLBAR_TOKEN,
}) => {
  const analytics = new GA4Events({ mode: 'client', gtmId: GTM_ID })
  const router = useRouter()
  const cart = useCart()
  const wishlist = useWishlist()
  const { showToaster } = useUi()
  const [renderKey, setRenderKey] = useState(null)
  const [currentForm, setCurrentForm] = useState<null | GravityFormsApiProps>(
    _default
  )
  const [fieldValues, setFieldValues] = useState({})
  const [fieldErrors, setFieldErrors] = useState({})
  const [loadingForm, setLoadingForm] = useState(true)
  const [loadingError, setLoadingError] = useState<boolean | string>(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitErrors, setSubmitErrors] = useState<boolean | string[]>(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [confirmations, setConfirmations] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [successPath, setSuccessPath] = useState('')
  const [formSaveExpiry, setFormSaveExpiry] = useState(0)
  const [currentTransactionId, setCurrentTransactionId] = useState(null)
  const [hiddenFieldTransactionId, setHiddenFieldTransactionId] = useState(null)
  const [previousTransactionId, setPreviousTransactionId] = useState(null)
  const [travelplanAttempt, setTravelplanAttempt] = useState(0)
  const [fromSummaryPage, setFromSummaryPage] = useState(false)
  const [summaryPageIndex, setSummaryPageIndex] = useState(null)
  const [formType, setFormType] = useState<
    null | 'bookingform' | 'requestform' | 'enquiryform'
  >(null)

  // Methods
  const initNewForm = async (
    id: number,
    redirect?: string,
    expiry?: number
  ) => {
    clearForm()
    const uid = getClientsideQueryString('uid')
    const redirectPath = stripWpUrl(redirect)

    try {
      const res = await renderForm({ id, query: uid ? { uid } : {} })

      if (res?.data) {
        const form = res.data as GravityFormsApiProps
        const { fields, confirmations, title, pagination } = form

        if (fields) {
          const withAccordions = gfGetAccordionFields(fields)
          form.fields = withAccordions
        }

        if (confirmations) {
          for (const [key, value] of Object.entries(confirmations)) {
            setConfirmations(value)
          }
        }

        setCurrentForm(res.data)
        setLoadingError(false)
        setHiddenFields(res.data.fields)
        if (redirectPath) setSuccessPath(redirectPath)
        if (expiry) setFormSaveExpiry(expiry)

        const formSessionId = gfMakeSaveFormId(res.data, destination)

        const previouslySavedValues = gfRetrieveSavedForm(formSessionId)
        if (previouslySavedValues) {
          setFieldValues(previouslySavedValues)
        }

        const page = {
          number: currentPage,
          title: pagination?.pages ? pagination.pages[currentPage] : title,
        }

        if (BOOKING_FORM_TITLES.includes(title)) {
          // todo - check if we don't need a booking form start analytics event
          // is not there in the current app
          setFormType('bookingform')
        }
        if (REQUEST_FORM_TITLES.includes(title)) {
          const pageType = {
            number: 0,
            title: page.title,
          }
          analytics.requestFormStart(cart.cartItems, fieldValues, pageType)
          setFormType('requestform')
        }
      } else {
        setLoadingError('There was an error loading your form')
      }
      setLoadingForm(false)
    } catch (error) {
      setLoadingError(error.message)
      setLoadingForm(false)
    }
  }

  const setHiddenFields = (fields: GravityFormsAPIFieldProps[]) => {
    const cartItems: CartItemProps[] = cart?.cartItems

    fields.forEach((field: GravityFormsAPIFieldProps) => {
      // Set a user ID
      if (field.cssClass === HIDDEN_FIELDS.USER_ID) {
        const userId = getUserId()
        setFieldValues(prev => ({ ...prev, [field.id]: userId }))
      }
      // Set a transaction ID
      if (field.cssClass === HIDDEN_FIELDS.TRANSACTION_ID) {
        setHiddenFieldTransactionId(field.id)
        let transactionId = currentTransactionId
        if (!currentTransactionId) {
          transactionId = gfCreateTransactionId()
          setCurrentTransactionId(transactionId)
        }
        setFieldValues(prev => ({ ...prev, [field.id]: transactionId }))
      }
      if (cartItems) {
        // Set the product ID's
        if (field.cssClass === HIDDEN_FIELDS.PRODUCT_IDS) {
          const cart_ids = cartItems
            .filter(item => item.gid)
            .map(item => item.id || item.parent)
            .join(' ')

          setFieldValues(prev => ({ ...prev, [field.id]: cart_ids }))
        }
        // Set the product GID's
        if (field.cssClass === HIDDEN_FIELDS.PRODUCT_GIDS) {
          const cart_gids = cartItems
            .filter(item => item.gid)
            .map(item => item.gid)
            .join(' ')

          setFieldValues(prev => ({ ...prev, [field.id]: cart_gids }))
        }
      }
    })
    return fields
  }

  const checkRequiredFields = () => {
    const missingRequiredFields = []
    let fields = currentPageContent?.fields || currentForm.fields
    const sections = fields.filter(field => field.type === 'section')
    const sectionFields = []
    if (sections?.length) {
      sections.forEach(section => {
        const { rulesAreMet } = matchConditionalLogic(section.conditionalLogic)
        const visible = rulesAreMet
        if (visible && section.items?.length) {
          section.items.forEach(item => sectionFields.push(item))
        }
      })
    }

    if (sectionFields?.length) {
      fields = [...fields, ...sectionFields]
    }

    if (fields) {
      fields.forEach((field: GravityFormsAPIFieldProps) => {
        // If field is required, but no value has been set, create error
        const fieldHasValue = gfParseFieldValuesType(fieldValues?.[field.id])
        if (field.isRequired && !fieldHasValue) {
          const errorMessage = gfGetRequiredFieldMessage(field)
          const fieldIsVisible = gfGetFieldCurrentVisibility(field, fieldValues)
          if (fieldIsVisible) {
            missingRequiredFields.push({ key: field.id, error: errorMessage })
          }
        }
      })
    }

    if (missingRequiredFields.length) {
      const newErrors = {}
      missingRequiredFields.forEach(
        ({ key, error }) => (newErrors[key] = error)
      )

      setFieldErrors(prev => ({
        ...prev,
        ...newErrors,
      }))

      return true
    } else {
      return false
    }
  }

  const updateTransactionId = (newTransactionId: string) => {
    setCurrentTransactionId(newTransactionId)
    setPreviousTransactionId(newTransactionId)
    if (hiddenFieldTransactionId && fieldValues[hiddenFieldTransactionId]) {
      setFieldValues(prev => ({
        ...prev,
        [hiddenFieldTransactionId]: newTransactionId,
      }))
    }
  }

  const handlePageChange = (value: 'next' | 'previous') => {
    if (value === 'next') {
      const missingRequiredFields = checkRequiredFields()
      if (!missingRequiredFields) {
        if (fromSummaryPage && summaryPageIndex) {
          // If we returned to a page to edit details after the summary, we go back to the summary, and reset fromSummaryPage value
          setCurrentPage(summaryPageIndex)
          setFromSummaryPage(false)
          scrollToTop()
        } else {
          // Otherwise we just go to the next page
          setCurrentPage(() => currentPage + 1)
          scrollToTop()
        }
        const page = {
          number: currentPage,
          title: currentPageContent.title,
        }
        if (formType === 'bookingform')
          analytics.bookingFormPage(page, currentTransactionId)
        if (!previousTransactionId && fieldValues[TRAVELPLAN_FIELD_ID])
          fetchTravelPlanDetails()
        if (formType === 'requestform')
          analytics.requestFormPage(cart.cartItems, fieldValues, page)
      } else {
        showToaster({
          severity: 'error',
          title: 'Let op, sommige verplichte velden zijn nog niet ingevuld',
        })
      }
    }
    if (value === 'previous') {
      setCurrentPage(() => currentPage - 1)
      scrollToTop()
    }
  }

  const fetchTravelPlanDetails = async () => {
    const travelPlanId = gfParseFieldValuesType(
      fieldValues[TRAVELPLAN_FIELD_ID]
    )
    const uid = getClientsideQueryString('uid')

    // Try at least 3 times (each page change)
    if (
      travelPlanId &&
      uid &&
      travelplanAttempt < 3 &&
      !previousTransactionId
    ) {
      const res = await travelPlan({
        uid,
        travelPlanId: travelPlanId.split('/')[0],
      })
      if (!res) {
        setTravelplanAttempt(1)
      }
      if (res) {
        updateTransactionId(res.transactionID)
      }
    }
  }

  const submitForm = async () => {
    if (!currentForm.fields) return null
    const missingRequiredFields = checkRequiredFields()
    if (missingRequiredFields) {
      showToaster({
        severity: 'error',
        title: translate('PAGE_MISSING_REQUIRED_FIELDS'),
      })
      scrollToTop()
      return null
    }

    setSubmitting(true)

    // Todo - check if this is the right place
    const uid = getClientsideQueryString('uid')
    const formId = currentForm?.id
    const payload = gfTransformFieldValuesToPayload(fieldValues)

    const result = await API.Forms.submit({
      formId,
      payload,
      params: uid ? { uid } : null,
    })

    const { error, data } = result
    const is_valid = data?.isValid || data?.response?.is_valid
    const validation_messages = data?.response?.validation_messages
    const errorFieldPage = data?.response?.source_page_number
    const confirmation_message = data?.confirmation_message

    if (!validation_messages && error) {
      dispatchRollbarError(
        `Submit Form Error: ${error?.message || result?.message}`,
        ROLLBAR_TOKEN,
        ROLLBAR_ENV
      )
      setSubmitErrors([error?.message])
      setSubmitting(false)
      showToaster({
        severity: 'error',
        title: 'Er is iets mis gegaan',
        content: error?.message || result?.message,
      })
      return false
    }

    if (validation_messages) {
      const messages = []
      const messagesWithKeys = []
      setFieldErrors(prev => ({ ...prev, ...validation_messages }))
      Object.keys(validation_messages).forEach(id => {
        const message = validation_messages[id]
        messages.push(message)
        messagesWithKeys.push(`${id}: ${message}`)
      })
      dispatchRollbarError(
        `Submit Form Validation: ${messagesWithKeys?.join(',')}`,
        ROLLBAR_TOKEN,
        ROLLBAR_ENV
      )
      setSubmitErrors(messages)
      setSubmitting(false)
      showToaster({
        severity: 'error',
        title: translate('FORM_MISSING_REQUIRED_FIELDS'),
      })

      if (errorFieldPage) {
        if (errorFieldPage === currentPage) {
          scrollToTop()
        } else {
          setCurrentPage(errorFieldPage)
        }
      }

      return false
    }

    if (!validation_messages || is_valid) {
      const page = {
        number: currentPage,
        title: 'Booking completed',
      }
      if (formType === 'bookingform') {
        analytics.bookingFormPage(page, currentTransactionId, true)
      }
      if (formType === 'requestform')
        analytics.requestFormSubmit(
          cart.cartItems,
          fieldValues,
          currentTransactionId,
          page
        )

      setSubmitSuccess(confirmation_message)
      setSubmitting(false)
      if (cart?.cartItems?.length && wishlist) {
        cart?.cartItems?.forEach(item => wishlist.removeFromWishlist(item))
      }
      cart.clearCart()
      gfClearPreviouslySavedForm(gfMakeSaveFormId(currentForm, destination))
      if (successPath) {
        router.push(successPath)
      } else {
        return true
      }
    }
  }

  const clearForm = () => {
    setCurrentForm(null)
    setFieldValues({})
    setFieldErrors({})
    setSuccessPath('')
  }

  const updateFieldValue = (
    fieldId: string | number,
    value: any,
    validation: RegExp,
    isNestedField = false,
    skipSanitize = false
  ) => {
    const newValue =
      typeof value === 'string' && !skipSanitize ? sanitizeInput(value) : value
    if (!currentForm) {
      console.warn('Cannot set a field on an empty form')
      return { valid: false }
    }
    const fields = currentForm.fields

    if (!fields || fields.length === 0) {
      console.warn('Current form does not contain any fields')
      return { valid: false }
    }

    if (validation) {
      const inputIsValid = gfValidateField(newValue, validation)
      if (!inputIsValid) return { valid: false }
    }

    if (isNestedField && typeof fieldId === 'string' && fieldId.includes('.')) {
      const split = fieldId.split('.')
      const parent = split[0]
      const child = fieldId.replace('.', '_')
      setFieldValues(prev => ({
        ...prev,
        [parent]: newValue,
        [child]: newValue,
      }))
      // Remove both the child from errors, and remove the parent from errors (i.e fieldId: 401 and fieldId: 401_1)
      setFieldErrors(prev => ({ ...prev, [parent]: '', [child]: '' }))
      setRenderKey(newValue)
    } else {
      if (fieldValues[fieldId] === newValue) return { valid: true }
      setFieldValues(prev => ({ ...prev, [fieldId]: newValue }))
      setFieldErrors(prev => ({ ...prev, [fieldId]: '' }))
      setRenderKey(newValue)
    }

    return { valid: true }
  }

  const matchConditionalLogic = useCallback(
    (logic: GravityFormsAPIConditionalLogicProps) => {
      return gfLoopConditionalRules(logic, fieldValues)
    },
    [fieldValues]
  )

  const formPages = useMemo(() => {
    if (!currentForm?.pagination && !currentForm?.pagination?.pages) return null
    if (!currentForm?.fields) return null
    return gfTransformPages(currentForm)
  }, [currentForm])

  const currentPageContent = useMemo(() => {
    if (!formPages || formPages.length === 0) return null
    return formPages.find(page => page.pageNumber === currentPage)
  }, [formPages, currentPage])

  const totalFormPages = useMemo(() => {
    if (!formPages) return 1
    return formPages.length
  }, [formPages])

  useEffect(() => {
    if (fieldValues && formSaveExpiry) {
      const localStorageSaveFormId = gfMakeSaveFormId(currentForm, destination)
      gfSaveForm({
        id: localStorageSaveFormId,
        expiry: formSaveExpiry,
        fieldValues,
      })
    }
  }, [fieldValues])

  const value = {
    renderKey,
    loadingForm,
    loadingError,
    currentForm,
    currentPageContent,
    formPages,
    totalFormPages,
    fieldValues,
    fieldErrors,
    submitting,
    submitErrors,
    submitSuccess,
    confirmations,
    fromSummaryPage,
    summaryPageIndex,
    initNewForm,
    handlePageChange,
    submitForm,
    clearForm,
    updateFieldValue,
    matchConditionalLogic,
    setCurrentPage,
    setFromSummaryPage,
    setSummaryPageIndex,
  }

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>
}

export const useForm = () => {
  return useContext(FormContext)
}
